import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { AppPlatformService } from 'src/app/service/platform/app-platform.service';
import { environment } from 'src/environments/environment';

import { BridgeEmbedAdapter } from './adapters/bridge.adapter';
import { VimeoEmbedAdapter } from './adapters/vimeo.adapter';
import { YoutubeEmbedAdapter } from './adapters/youtube.adapter';
import { embedTransport } from './embed-transport.policy';
import { EmbedPlayerEvent, EmbedProvider, EmbedVideoAdapter, WatchProgress } from './embed-video.model';
import { WatchCompletionTracker } from './watch-completion.tracker';

/**
 * Cross-platform youtube/vimeo player. Reusable outside the learning path by design (see the plan,
 * Part A): same input/output names as `app-custom-video-player` so a classic course could swap its
 * iframe for this component without learning new vocabulary — it is not wired into `pages/tema` in
 * this phase, only built to make that swap painless later.
 */
@Component({
  selector: 'app-embed-video-player',
  templateUrl: './embed-video-player.component.html',
  styleUrls: ['./embed-video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbedVideoPlayerComponent implements OnChanges, OnDestroy {
  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;

  @Input() provider!: EmbedProvider;
  @Input() videoId = '';
  /** Kept for contract parity with `app-custom-video-player`; the provider shows its own thumbnail. */
  @Input() posterSrc = '';
  @Input() playerId = 'embed-player';
  @Input() savedCurrentTime = 0;
  @Input() reviewAllIsRequired = false;

  @Output() videoEnded = new EventEmitter<{ currentTime: number }>();
  @Output() videoPaused = new EventEmitter<number>();
  @Output() videoPlaying = new EventEmitter<number>();
  @Output() videoWWillEnd = new EventEmitter<boolean>();
  /** Raw tracker samples — not needed by the learning path today, exposed for a future caller. */
  @Output() watchProgress = new EventEmitter<WatchProgress>();
  @Output() playerError = new EventEmitter<unknown>();
  /** The adapter finished mounting. Used by `media-embed-bridge` to answer the parent's `ready`. */
  @Output() playerReady = new EventEmitter<void>();

  private adapter: EmbedVideoAdapter | null = null;
  private tracker: WatchCompletionTracker | null = null;
  private fellBackToPlainIframe = false;

  constructor(private readonly zone: NgZone, private readonly appPlatform: AppPlatformService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['provider'] || changes['videoId']) {
      this.remount();
    }
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  private remount(): void {
    this.teardown();
    this.fellBackToPlainIframe = false;
    if (!this.provider || !this.videoId) return;

    this.tracker = new WatchCompletionTracker(
      this.zone,
      this.reviewAllIsRequired,
      {
        onProgress: (progress) => this.watchProgress.emit(progress),
        onNearEnd: () => this.videoWWillEnd.emit(true),
        onCompleted: () => this.videoEnded.emit({ currentTime: this.adapter?.getCurrentTime() ?? 0 }),
      },
      this.savedCurrentTime
    );

    this.adapter =
      embedTransport(this.appPlatform) === 'bridge'
        ? new BridgeEmbedAdapter(this.bridgeUrl(), () => this.fallbackToPlainIframe())
        : this.provider === 'vimeo'
        ? new VimeoEmbedAdapter()
        : new YoutubeEmbedAdapter();

    const host = this.hostRef.nativeElement;
    host.innerHTML = '';

    this.adapter.mount(host, {
      provider: this.provider,
      videoId: this.videoId,
      playerId: this.playerId,
      startAt: this.savedCurrentTime,
      onEvent: (event) => this.handleAdapterEvent(event),
    });

    this.tracker.start(
      () => this.adapter?.getCurrentTime() ?? 0,
      () => this.adapter?.getDuration() ?? 0
    );
  }

  private handleAdapterEvent(event: EmbedPlayerEvent): void {
    switch (event.type) {
      case 'playing':
        this.videoPlaying.emit();
        break;
      case 'paused':
        this.videoPaused.emit(event.currentTime);
        break;
      case 'ended':
        this.tracker?.notifyEnded();
        break;
      case 'error':
        this.playerError.emit(event.error);
        break;
      case 'ready':
        this.playerReady.emit();
        break;
    }
  }

  /** Commands the bridge forwards from the parent page (`seek`/`play`/`pause`). */
  seekTo(seconds: number): void {
    this.adapter?.seekTo(seconds);
  }

  play(): void {
    this.adapter?.play();
  }

  pause(): void {
    this.adapter?.pause();
  }

  /** The bridge did not answer within its watchdog — fall back to a plain iframe (no detection, but
   *  not a blank box either) rather than leave the learner looking at nothing. */
  private fallbackToPlainIframe(): void {
    if (this.fellBackToPlainIframe) return;
    this.fellBackToPlainIframe = true;
    this.teardown();

    const host = this.hostRef.nativeElement;
    host.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.src =
      this.provider === 'vimeo'
        ? `https://player.vimeo.com/video/${this.videoId}?autopause=0&dnt=1`
        : `https://www.youtube.com/embed/${this.videoId}?playsinline=1&rel=0`;
    host.appendChild(iframe);
  }

  private bridgeUrl(): string {
    const base = environment.APP_MULTIMARCA ? 'https://app.cursalab.io/' : environment.URL_BASE;
    return `${base}media-embed-bridge`;
  }

  private teardown(): void {
    this.tracker?.stop();
    this.tracker = null;
    this.adapter?.destroy();
    this.adapter = null;
  }
}
